/** @jsxImportSource @emotion/react */

import { Globe, Monitor, Plus } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { SyncLoader } from 'react-spinners';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';

import { useAppDispatch } from 'store/hooks';
import { history } from 'store/store';
import { theme } from 'styles/theme/theme';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { resetPressedState, setActiveBoard, setActiveTemplate } from '../../store/templates/templates';
import ListContainer from '../components/elements/list-container';
import TemplateItem from '../components/elements/template-item';
import useCreateModal from '../hooks/modals/template/create';
import useSuccessCopyModal from '../hooks/modals/template/success-copy';
import { useLoadTemplates } from '../hooks/use-load-templates';
import useTemplates from '../hooks/use-templates';

function Templates() {
  const state = useLocation().state;
  const dispatch = useAppDispatch();
  const { templates, isLoading: isTemplatesLoading } = useTemplates();
  const { modal: createModal, open: openCreateModal } = useCreateModal();
  const { modal: successCopyModal, open: openSuccessCopyModal } = useSuccessCopyModal();

  const { data: feature, isFetching: isFeatureFetching } = useFetchAvailableFeature();

  const { setHeaderContent } = useMainLayout();

  useLoadTemplates();

  const breadcrumb = useMemo(
    () => (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: 'Турниры', to: Pages.TOURNEYS.INDEX },
          { label: 'Шаблоны', to: Pages.TEMPLATE.INDEX },
        ]}
      />
    ),
    [],
  );

  const openCatalog = useCallback(() => {
    history.push(Pages.TEMPLATE.CATALOG.INDEX);
  }, []);

  useEffect(() => {
    if (state && state.isCoppedTemplate) {
      openSuccessCopyModal();
    }

    dispatch(setActiveTemplate({ id: undefined }));
    dispatch(setActiveBoard({ id: undefined }));
    dispatch(resetPressedState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  const body = useMemo(() => {
    if (isTemplatesLoading || isFeatureFetching) {
      return (
        <div className={'flex-center h-full w-full px-6'}>
          <SyncLoader />
        </div>
      );
    }

    return templates.map((template, index) => <TemplateItem key={template.id} template={template} index={index} />);
  }, [isFeatureFetching, isTemplatesLoading, templates]);

  return (
    <div className="flex h-full flex-col gap-4 px-6 pt-4">
      <div className={'flex w-full justify-end'} style={{ gap: 24 }}>
        <UDButton variant={'dark'} className="flex items-center" onClick={() => history.push(Pages.DEVICES.INDEX)}>
          <Monitor size={16} className={'mr-2'} />
          Устройства
        </UDButton>
        <UDButton
          variant={'dark'}
          className="flex items-center"
          onClick={openCatalog}
          disabled={isFeatureFetching || !feature?.OBS_INTEGRATION}
        >
          <Globe size={16} className={'mr-2'} />
          Готовые шаблоны
        </UDButton>
        <UDButton
          variant={'dark'}
          className="flex items-center"
          onClick={openCreateModal}
          disabled={isFeatureFetching || !feature?.OBS_INTEGRATION}
        >
          <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
          Добавить шаблон
        </UDButton>
      </div>
      <ListContainer className="flex-1" title="Шаблоны">
        {body}
      </ListContainer>
      {createModal}
      {successCopyModal}
    </div>
  );
}

export default Templates;
